import { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserAvatar } from "@/components/UserAvatar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Layout } from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";

type Profile = {
  id: string;
  user_type: "developer" | "creator" | "player";
  display_name: string;
  avatar_url: string | null;
  bio: string | null;
  website_url: string | null;
  studio_name: string | null;
  studio_logo_url: string | null;
  platform: string | null;
  channel_url: string | null;
  subscriber_count: number | null;
  preferred_genres: string[] | null;
  created_at: string | null;
};

export default function UsersList() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [userType, setUserType] = useState<"all" | "developer" | "creator" | "player">("all");

  const fetchProfiles = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("profiles")
        .select("id, user_type, display_name, avatar_url, bio, website_url, studio_name, studio_logo_url, platform, channel_url, subscriber_count, preferred_genres, created_at, active_border_id, shop_items(metadata)")
        .order("created_at", { ascending: false });

      if (userType !== "all") {
        query = query.eq("user_type", userType);
      }

      const { data, error } = await query;
      if (error) throw error;
      setProfiles((data as unknown as Profile[]) || []);
    } catch (e: any) {
      setError(e?.message || "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return profiles;
    return profiles.filter((p) =>
      [
        p.display_name,
        p.bio || "",
        p.website_url || "",
        p.studio_name || "",
        p.platform || "",
        p.channel_url || "",
        (p.preferred_genres || []).join(", ")
      ]
        .join(" ")
        .toLowerCase()
        .includes(term)
    );
  }, [profiles, search]);

  return (
    <Layout>
      <div className="flex items-center justify-center p-4">
        <Card className="w-full max-w-6xl p-6 card-gradient border-border/50">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between mb-4">
            <h1 className="text-2xl font-semibold">Utilisateurs</h1>
            <div className="flex gap-2 w-full md:w-auto">
              <Input
                placeholder="Rechercher (nom, bio, site, genres...)"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-background/50"
              />
              <Select value={userType} onValueChange={(v) => setUserType(v as any)}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Type d'utilisateur" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="developer">Développeurs</SelectItem>
                  <SelectItem value="creator">Créateurs</SelectItem>
                  <SelectItem value="player">Joueurs</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={fetchProfiles} disabled={loading} className="glow-effect">
                {loading ? "Chargement..." : "Rafraîchir"}
              </Button>
            </div>
          </div>

          {error ? (
            <div className="text-red-500 text-sm mb-3">{error}</div>
          ) : null}

          <div className="rounded-md border border-border/60 overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Profil</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Bio</TableHead>
                  <TableHead>Studio / Plateforme</TableHead>
                  <TableHead>Liens</TableHead>
                  <TableHead>Genres</TableHead>
                  <TableHead>Métriques</TableHead>
                  <TableHead>Créé le</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((p) => {
                  const initials = (p.display_name || "?")
                    .split(" ")
                    .map((s) => s[0])
                    .join("")
                    .slice(0, 2)
                    .toUpperCase();
                  return (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <UserAvatar
                            src={p.avatar_url || undefined}
                            fallback={initials}
                            size="sm"
                            borderStyle={(p as any).shop_items?.metadata?.style}
                          />
                          <div className="flex flex-col">
                            <div className="font-medium">{p.display_name}</div>
                            {p.website_url ? (
                              <a
                                href={p.website_url}
                                target="_blank"
                                rel="noreferrer"
                                className="text-xs text-primary hover:underline"
                              >
                                Site
                              </a>
                            ) : null}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={p.user_type === "developer" ? "default" : "secondary"}>
                          {p.user_type}
                        </Badge>
                      </TableCell>
                      <TableCell className="max-w-[320px] whitespace-pre-wrap">
                        {p.bio || "—"}
                      </TableCell>
                      <TableCell>
                        {p.user_type === "developer" ? (p.studio_name || "—") : (p.platform || "—")}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {p.channel_url ? (
                            <a href={p.channel_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                              Chaîne
                            </a>
                          ) : null}
                          {p.studio_logo_url ? (
                            <a href={p.studio_logo_url} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline">
                              Logo studio
                            </a>
                          ) : null}
                        </div>
                      </TableCell>
                      <TableCell>
                        {p.preferred_genres && p.preferred_genres.length ? p.preferred_genres.join(", ") : "—"}
                      </TableCell>
                      <TableCell>
                        {p.subscriber_count != null ? `${p.subscriber_count} abonnés` : "—"}
                      </TableCell>
                      <TableCell>
                        {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </Layout>
  );
}


